// A bounded register adapter. Language interpretation stays in the backend agent.
const registryLiveControlApi = (() => {
  let pageIntent: any = null
  const presentOnPage = (intent: any) => {
    pageIntent = intent
    window.dispatchEvent?.(new CustomEvent('plumme-registry-page-intent'))
  }
  const consumePageIntent = (kind: string) => {
    if (pageIntent?.kind !== kind) return null
    const intent = pageIntent
    pageIntent = null
    return intent
  }
  const routes: Record<string, string> = { customer: 'kunder', product: 'produkter', supplier: 'leverandorer' }
  const fields: Record<string, string[]> = {
    customer: ['name', 'org', 'email', 'phone', 'addr', 'zip', 'city', 'note', 'defaultCustomerReference', 'followUp', 'contactPersons'],
    supplier: ['name', 'org', 'email', 'phone', 'addr', 'zip', 'city', 'defaultDueDays', 'defaultCostAccountCode', 'defaultSettlementAccountCode'],
    product: ['name', 'desc', 'price', 'type', 'vatSelection', 'sku', 'ean', 'productGroup'],
  }
  const clone = (value: any) => JSON.parse(JSON.stringify(value))
  const normalized = (value: any) => String(value || '').trim().toLowerCase()
  const archived = (row: any) => row?.arkivert === true || ['archived', 'passiv'].includes(row?.status)
  const mutations = new Set(['save_record', 'archive_record', 'restore_record'])
  const create = (deps: any) => {
    let state: any = { kind: '', matches: [], total: 0, draft: null, dirty: false, isNew: false, receipt: null, uncertain: false }
    let baseline = '', revision = 0, busy = false
    const changedFields = new Set<string>()
    const listeners = new Set<() => void>()
    const publish = (patch: any) => { state = { ...state, ...patch }; revision++; listeners.forEach(fn => fn()) }
    const records = (kind: string) => deps.records(kind)
    const current = () => { if (!deps.isCurrent()) throw new Error('control.editorChanged') }
    const compact = (row: any, kind: string) => Object.fromEntries(['id', 'status', ...fields[kind]].filter(key => row[key] !== undefined).map(key => [key, row[key]]))
    const snapshot = () => ({
      key: JSON.stringify([revision, state.draft?.id && records(state.kind).find((r: any) => r.id === state.draft.id)]),
      ui: { kind: state.kind, matches: state.matches, total: state.total,
        draft: state.draft ? compact(state.draft, state.kind) : null,
        isNew: state.isNew, dirty: state.dirty, receipt: state.receipt, uncertain: state.uncertain },
    })
    const unchanged = () => {
      if (!state.draft || state.isNew) return
      const latest = records(state.kind).find((r: any) => r.id === state.draft.id)
      if (!latest || JSON.stringify(latest) !== baseline) throw new Error('registry.changed')
    }
    const select = (kind: string, id: string) => {
      if (!state.matches.some((r: any) => r.id === id)) throw new Error('registry.selectFirst')
      const row = records(kind).find((r: any) => r.id === id)
      if (!row) throw new Error('registry.selectFirst')
      baseline = JSON.stringify(row)
      changedFields.clear()
      publish({ draft: clone(row), dirty: false, isNew: false, receipt: null, uncertain: false })
    }
    const patchFields = (patch: any) => {
      if (!state.draft || !patch || typeof patch !== 'object' || Array.isArray(patch)) throw new Error('registry.selectFirst')
      if (archived(state.draft)) throw new Error('registry.archived')
      for (const [key, value] of Object.entries(patch)) {
        const contactsValid = key === 'contactPersons' && Array.isArray(value) && value.length <= 30 && value.every((contact: any) => contact && typeof contact === 'object' && !Array.isArray(contact) && Object.keys(contact).every(field => ['id', 'name', 'role', 'email', 'phone'].includes(field)) && Object.values(contact).every(field => typeof field === 'string' && field.length <= 2000))
        const numberValid = ['price', 'defaultDueDays'].includes(key) && typeof value === 'number' && Number.isFinite(value) && value >= 0 && (key !== 'defaultDueDays' || value <= 365)
        if (!fields[state.kind].includes(key) || (key === 'contactPersons' ? !contactsValid : ['price', 'defaultDueDays'].includes(key) ? !numberValid : typeof value !== 'string' || value.length > 2000)) throw new Error('registry.unsupportedField')
        if (state.kind === 'product' && !state.isNew && ['type', 'vatSelection'].includes(key) && value !== state.draft[key]) throw new Error('registry.advancedProduct')
      }
      Object.keys(patch).forEach(key => changedFields.add(key))
      publish({ draft: { ...state.draft, ...patch }, dirty: true, receipt: null, changedField: Object.keys(patch)[0] })
    }
    const perform = async (step: any, question: string, manuallyApproved = false) => {
      current()
      if (busy) throw new Error('live.actionBusy')
      if (!Object.hasOwn(routes, step.kind)) throw new Error('registry.unsupported')
      const kind = step.kind
      if (!['search', 'lookup_brreg', 'open_record', 'open_merge', 'new_record', 'set_fields', ...mutations].includes(step.action)) throw new Error('registry.unsupported')
      if (mutations.has(step.action)) {
        const quote = String(step.authorizationQuote || '').trim()
        if (!manuallyApproved && (quote.length < 4 || !question.includes(quote))) throw new Error('registry.authorization')
        // Current services own country-specific defaults. Do not introduce those in another jurisdiction.
        if (deps.country() !== 'NO') throw new Error('registry.country')
        if (state.uncertain) throw new Error('registry.uncertain')
      }
      if (step.action === 'search') {
        if (state.dirty) throw new Error('registry.unsaved')
        const query = normalized(step.query)
        const matches = records(kind).filter((r: any) => !query || [r.name, r.org, r.sku, r.nr].some(v => normalized(v).includes(query)))
        deps.navigate(routes[kind])
        publish({ kind, matches: matches.slice(0, 15).map((r: any) => ({ id: r.id, source: 'plumme', name: r.name, org: r.org || '', status: archived(r) ? 'archived' : 'active', ...(kind === 'customer' ? { outstanding: r.out, sales: r.sales } : kind === 'supplier' ? { outstanding: r.out, purchases: r.buy } : {}) })), total: matches.length, draft: null, receipt: null })
        presentOnPage({ action: 'search', kind, query: String(step.query || '') })
        return { action: step.action, kind, result: 'observed', source: 'plumme', query, count: matches.length }
      }
      if (step.action === 'lookup_brreg') {
        if (state.dirty) throw new Error('registry.unsaved')
        if (!['customer', 'supplier'].includes(kind) || deps.country() !== 'NO') throw new Error('registry.country')
        const query = String(step.query || '').trim()
        if (query.length < 2) throw new Error('control.incomplete')
        const backend = deps.backend()
        if (typeof backend.lookupBusinessRegistry !== 'function') throw new Error('registry.unavailable')
        const matches = (await backend.lookupBusinessRegistry(query, 'NO')).slice(0, 8).map((row: any) => ({
          id: `brreg:${String(row.organizationNumber || row.org || row.id || '')}`,
          source: 'brreg', name: String(row.businessName || row.name || ''), org: String(row.organizationNumber || row.org || ''),
          email: String(row.email || ''), phone: String(row.phone || ''), addr: String(row.address || row.addr || ''),
          zip: String(row.postalCode || row.zip || ''), city: String(row.postalPlace || row.city || ''), status: 'registry',
        })).filter((row: any) => row.name && row.org)
        deps.navigate(routes[kind])
        publish({ kind, matches, total: matches.length, draft: null, receipt: null })
        return { action: step.action, kind, result: 'observed', source: 'brreg', query, count: matches.length }
      }
      if (state.kind !== kind || deps.getRoute() !== routes[kind]) throw new Error('registry.selectFirst')
      if (step.action === 'open_merge') {
        if (!['customer', 'supplier'].includes(kind) || !step.recordId) throw new Error('registry.unsupported')
        const row = records(kind).find((record: any) => record.id === step.recordId)
        if (!row) throw new Error('registry.selectFirst')
        presentOnPage({ action: 'open_merge', kind, recordId: step.recordId })
        return { action: step.action, kind, result: 'merge_modal_opened', id: step.recordId }
      }
      if (step.action === 'open_record' || step.action === 'new_record') {
        if (state.dirty) throw new Error('registry.unsaved')
        if (step.action === 'open_record') { select(kind, step.recordId); presentOnPage({ action: 'open_record', kind, recordId: step.recordId }) }
        else {
          const registryMatch = step.recordId && state.matches.find((row: any) => row.id === step.recordId && row.source === 'brreg')
          if (step.recordId && !registryMatch) throw new Error('registry.selectFirst')
          baseline = ''; changedFields.clear()
          const draft = registryMatch
            ? Object.fromEntries(['name', 'org', 'email', 'phone', 'addr', 'zip', 'city'].map(key => [key, registryMatch[key] || '']))
            : {}
          Object.keys(draft).forEach(key => changedFields.add(key))
          publish({ draft: { id: crypto.randomUUID(), status: 'aktiv', ...draft }, isNew: true, dirty: true, receipt: null, uncertain: false })
        }
        return { action: step.action, kind, result: 'opened' }
      }
      unchanged()
      if (step.action === 'set_fields') {
        patchFields(step.fields)
        return { action: step.action, kind, result: 'applied' }
      }
      if (!state.draft || (step.recordId && step.recordId !== state.draft.id)) throw new Error('registry.selectFirst')
      const draft = clone(state.draft)
      if (step.action !== 'save_record' && (!step.recordId || state.isNew || state.dirty)) throw new Error('registry.selectFirst')
      if (step.action === 'restore_record' && !['supplier', 'product'].includes(kind)) throw new Error('registry.unsupported')
      if ((step.action === 'archive_record' && archived(draft)) || (step.action === 'restore_record' && !archived(draft))) return { action: step.action, kind, result: 'unchanged', id: draft.id }
      if (step.action === 'save_record') {
        if (!state.dirty && !state.isNew) return state.receipt || { action: step.action, kind, result: 'unchanged', id: draft.id }
        if (!String(draft.name || '').trim()) throw new Error('control.incomplete')
        if (archived(draft)) throw new Error('registry.archived')
        const duplicates = records(kind).filter((r: any) => r.id !== draft.id &&
          (normalized(r.name) === normalized(draft.name) || (draft.org && normalized(r.org) === normalized(draft.org))))
        if (duplicates.length) throw new Error('registry.duplicate')
        if (kind === 'product') {
          if (!['Vare', 'Tjeneste'].includes(draft.type)) throw new Error('registry.advancedProduct')
          if (!Number.isFinite(draft.price) || draft.price < 0 || !deps.backend().getSalesVatOptions().some((v: any) => String(v.value) === draft.vatSelection)) throw new Error('control.incomplete')
        }
      }
      busy = true
      try {
        const names: Record<string, string> = { customer: 'Customer', supplier: 'Supplier', product: 'Product' }
        const backend = deps.backend()
        current(); unchanged()
        const result = step.action === 'archive_record'
          ? await backend[`archive${names[kind]}Record`](draft.id)
          : await backend[`save${names[kind]}Record`](step.action === 'restore_record' ? { ...draft, status: 'aktiv' } : draft,
            step.action === 'save_record' ? { fields: [...changedFields] } : {})
        if (!result?.id || result.id !== draft.id) throw new Error('control.failed')
        if (!deps.isCurrent()) return { action: step.action, kind, result: 'saved', id: result.id, name: result.name }
        const row = records(kind).find((r: any) => r.id === result.id) || result
        if (step.action === 'save_record' && [...changedFields].some(key => {
          const actual = row[key], expected = draft[key]
          return typeof expected === 'number' ? actual !== expected : String(actual ?? '').trim() !== String(expected ?? '').trim()
        })) throw new Error('live.notApplied')
        if (step.action === 'archive_record' && !archived(row)) throw new Error('live.notApplied')
        if (step.action === 'restore_record' && archived(row)) throw new Error('live.notApplied')
        const receipt = { action: step.action, kind, result: step.action === 'archive_record' ? 'archived' : step.action === 'restore_record' ? 'restored' : 'saved', id: result.id, name: result.name }
        if (deps.isCurrent()) {
          baseline = JSON.stringify(row)
          changedFields.clear()
          publish({ draft: clone(row), isNew: false, dirty: false, receipt, uncertain: false })
        }
        return receipt
      } catch (error) {
        // A rejected/uncertain service receipt is never automatically replayed by the agent.
        publish({ uncertain: true })
        throw error
      } finally { busy = false }
    }
    const prepareVisibleSave = (step: any, question: string) => {
      current()
      if (busy || step?.action !== 'save_record' || !Object.hasOwn(routes, step?.kind)) throw new Error('registry.unsupported')
      const quote = String(step.authorizationQuote || '').trim()
      if (quote.length < 4 || !question.includes(quote)) throw new Error('registry.authorization')
      if (deps.country() !== 'NO') throw new Error('registry.country')
      if (state.uncertain) throw new Error('registry.uncertain')
      if (state.kind !== step.kind || deps.getRoute() !== routes[step.kind] || !state.dirty || !state.draft) throw new Error('registry.selectFirst')
      unchanged()
      const draft = clone(state.draft)
      if (step.recordId && step.recordId !== draft.id) throw new Error('registry.selectFirst')
      if (!String(draft.name || '').trim()) throw new Error('control.incomplete')
      if (archived(draft)) throw new Error('registry.archived')
      const duplicates = records(step.kind).filter((row: any) => row.id !== draft.id &&
        (normalized(row.name) === normalized(draft.name) || (draft.org && normalized(row.org) === normalized(draft.org))))
      if (duplicates.length) throw new Error('registry.duplicate')
      if (step.kind === 'product' && (!Number.isFinite(draft.price) || draft.price < 0 ||
        !deps.backend().getSalesVatOptions().some((value: any) => String(value.value) === draft.vatSelection))) throw new Error('control.incomplete')
      return { draft, isNew: state.isNew }
    }
    return { perform, prepareVisibleSave, snapshot, fields, routes, isMutation: (step: any) => mutations.has(step.action),
      getSnapshot: () => state, subscribe: (fn: () => void) => { listeners.add(fn); return () => listeners.delete(fn) },
      edit: (key: string, value: any) => {
        current()
        if (busy) return
        unchanged()
        if (state.kind === 'product' && state.draft && key === 'price' && value === '') {
          changedFields.add(key); publish({ draft: { ...state.draft, price: '' }, dirty: true, receipt: null, changedField: key })
        } else patchFields({ [key]: value })
      },
      close: () => { if (!busy) { baseline = ''; changedFields.clear(); publish({ kind: '', draft: null, matches: [], total: 0, dirty: false, receipt: null }) } },
    }
  }
  return { create, consumePageIntent, presentPageIntent: presentOnPage }
})()
Object.assign(globalThis, { PlummeRegistryLiveControl: registryLiveControlApi })
